import { Box, Text, VStack, HStack, Circle } from '@chakra-ui/react';
import SkeletonCard from './dashboard-skeleton-card';
import DashboardCard from './dashboard-card';
import { DASHBOARD_COLORS } from './dashboard-colors';

interface InteractionsChartProps {
  used: number;
  limit: number;
  isLoading: boolean;
}

export default function InteractionsChart({
  used,
  limit,
  isLoading,
}: InteractionsChartProps) {
  if (isLoading) {
    return <SkeletonCard />;
  }

  const percentage = limit > 0 ? (used / limit) * 100 : 0;
  const percentageDisplay = percentage.toFixed(2);
  const isNearLimit = percentage >= 80;
  const isAtLimit = percentage >= 95;

  const getStatusColor = () => {
    if (isAtLimit) return DASHBOARD_COLORS.error;
    if (isNearLimit) return DASHBOARD_COLORS.warning;
    return DASHBOARD_COLORS.primary;
  };

  const getStatusText = () => {
    if (isAtLimit) return 'Critical';
    if (isNearLimit) return 'Warning';
    return 'Normal';
  };

  return (
    <DashboardCard title="Interactions Used">
      <VStack gap={9} align="stretch" justifyContent="center" height="100%">
        {/* Status indicator */}
        <HStack justify="space-between" align="center">
          <HStack>
            <Circle size="3" bg={getStatusColor()} />
            <Text fontSize="sm" color="gray.600" fontWeight="medium">
              {getStatusText()}
            </Text>
          </HStack>
          <Text fontSize="sm" color="gray.500">
            {percentageDisplay}% used
          </Text>
        </HStack>

        {/* Main value display */}
        <VStack gap={2}>
          <Text
            fontSize="3xl"
            fontWeight="bold"
            color="gray.800"
            textAlign="center"
          >
            {used.toLocaleString()}
          </Text>
          <Text fontSize="md" color="gray.500" textAlign="center">
            of {limit.toLocaleString()} interactions
          </Text>
        </VStack>

        {/* Enhanced progress bar with markers */}
        <VStack gap={3} width="100%">
          {/* Progress bar container */}
          <Box width="100%" position="relative">
            {/* Background track with subtle gradient */}
            <Box
              width="100%"
              height="12px"
              background="linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)"
              borderRadius="full"
              position="relative"
              overflow="hidden"
              boxShadow="inset 0 2px 4px rgba(0,0,0,0.1)"
            >
              {/* Progress fill with enhanced gradient */}
              <Box
                width={`${percentageDisplay}%`}
                height="100%"
                borderRadius="full"
                transition="all 0.3s ease"
                position="relative"
                background={
                  isAtLimit
                    ? `linear-gradient(135deg, ${DASHBOARD_COLORS.warning} 0%, ${DASHBOARD_COLORS.error} 50%, #dc2626 100%)`
                    : isNearLimit
                      ? `linear-gradient(135deg, ${DASHBOARD_COLORS.primary} 0%, ${DASHBOARD_COLORS.warning} 70%, #f59e0b 100%)`
                      : `linear-gradient(135deg, ${DASHBOARD_COLORS.primary} 0%, #2563eb 30%, ${DASHBOARD_COLORS.secondary} 100%)`
                }
                boxShadow={
                  isAtLimit
                    ? `0 0 20px ${DASHBOARD_COLORS.error}40`
                    : isNearLimit
                      ? `0 0 15px ${DASHBOARD_COLORS.warning}30`
                      : `0 0 10px ${DASHBOARD_COLORS.primary}20`
                }
                _before={{
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '50%',
                  borderRadius: 'full',
                  background:
                    'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 100%)',
                  pointerEvents: 'none',
                }}
              />

              {/* Progress markers */}
              <Box
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
              >
                {/* 50% marker */}
                <Box
                  position="absolute"
                  left="50%"
                  top="0"
                  width="2px"
                  height="100%"
                  bg="white"
                  opacity={0.7}
                  transform="translateX(-1px)"
                />
                {/* 80% marker (warning threshold) */}
                <Box
                  position="absolute"
                  left="80%"
                  top="0"
                  width="3px"
                  height="100%"
                  bg="white"
                  opacity={0.9}
                  transform="translateX(-1.5px)"
                />
              </Box>
            </Box>

            {/* Threshold labels */}
            <Box position="relative" mt={1} height="16px">
              <Text
                fontSize="xs"
                color="gray.400"
                position="absolute"
                // left="0"
                transform="translateX(-50%)"
              >
                0%
              </Text>
              <Text
                fontSize="xs"
                color="gray.400"
                position="absolute"
                left="51%"
                transform="translateX(-50%)"
              >
                50%
              </Text>
              <Text
                fontSize="xs"
                color="gray.400"
                fontWeight="semibold"
                position="absolute"
                left="81%"
                transform="translateX(-50%)"
              >
                80%
              </Text>
              <Text
                fontSize="xs"
                color="gray.400"
                position="absolute"
                left="100%"
                transform="translateX(-50%)"
              >
                100%
              </Text>
            </Box>
          </Box>

          {/* Current percentage display */}
          <Box textAlign="center">
            <Text fontSize="lg" fontWeight="bold" color={getStatusColor()}>
              {percentageDisplay}% Usage
            </Text>
          </Box>
        </VStack>

        {/* Remaining interactions */}
        <Box textAlign="center">
          <Text fontSize="sm" color="gray.600">
            <Text as="span" fontWeight="semibold" color={getStatusColor()}>
              {(limit - used).toLocaleString()}
            </Text>{' '}
            interactions remaining
          </Text>
        </Box>
      </VStack>
    </DashboardCard>
  );
}
